import { Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PatientOrder,
  PatientSubscription,
  PrescriptionMedication,
} from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import WhiteCard from "src/shared/components/WhiteCard";

interface MedicationCardProps {
  details: PrescriptionMedication;
  subscription: PatientSubscription | null;
  page: string;
  order: PatientOrder;
  pastOrder?: boolean;
}

const MedicationCard = ({
  subscription,
  page,
  details,
  order,
  pastOrder = false,
}: MedicationCardProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const component = "medicationCard";

  const locale = usePatientLocale();

  const refillDate = useMemo(() => {
    if (pastOrder) return null;

    if (subscription?.current_period_end == null) return null;

    const dateFormat =
      locale === enUS ? "EEEE, MMMM dd, yyyy" : "EEEE, dd 'de' MMMM, yyyy";

    return format(new Date(subscription.current_period_end), dateFormat, {
      locale,
    });
  }, [locale, pastOrder, subscription?.current_period_end]);

  const medicationName = useMemo(() => {
    if (subscription?.product.slug === "bc") {
      return subscription.product.display_name;
    }

    return details.medications[0]?.display_name ?? "";
  }, [
    details.medications,
    subscription?.product.display_name,
    subscription?.product.slug,
  ]);

  const productSlug = useMemo(() => {
    if (order == null) return "";

    return order.product.slug;
  }, [order]);

  return (
    <Stack gap={3}>
      <WhiteCard sx={{ padding: "4px" }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <MedicationIcon
            name={medicationName}
            width={isMobile ? "120px" : "130px"}
            height={isMobile ? "120px" : "180px"}
            slug={productSlug}
          />
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {t(`medications.${medicationName.toLowerCase()}.capitalized`)}
            </Typography>
            {order.product.billing_model === "one_time" ? (
              <Typography>{t(`${page}.${component}.refillMessage`)}</Typography>
            ) : null}
            {refillDate ? (
              <Typography>
                {t(`${page}.${component}.refill`, {
                  date: refillDate,
                })}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </WhiteCard>
    </Stack>
  );
};

export default MedicationCard;
